import IKeyValue from '@sunbit-dev/common/models/IKeyValue';
import { datadogRum } from '@sunbit-dev/common/services/datadog';
import { isEmbeddedMode } from '@sunbit-dev/purchase-common/src/helpers/embeddedMode';
import { EventsNames } from '~/enums/Events';
import PlaceInFlowPages from './PlaceInFlowPages';

/* eslint-disable no-console */

export interface IMessageData extends IKeyValue {
  type: MessageDataType;
  checkoutContext: string;
}

export enum MessageDataType {
  INIT = 'INIT',
  REQUEST_INIT = 'REQUEST_INIT',
  DONE = 'DONE',
  CANCEL = 'CANCEL',
  USER_CANCEL = 'USER_CANCEL',
  PLACE_IN_FLOW = 'PLACE_IN_FLOW',
  PURCHASE_VERIFIED = 'PURCHASE_VERIFIED',
}

export default class MessageService {
  private originWindow?: MessageEventSource;

  private checkoutContext?: string;

  private targetOrigin = { targetOrigin: '*' };

  private initRetryTimer?: ReturnType<typeof setInterval>;

  private initReceived = false;

  private requestInitAttempts = 0;

  private readonly constructedAt = Date.now();

  public constructor() {
    window.addEventListener('message', this.handleMessages, false);
    this.requestInit();
  }

  private requestInit() {
    if (!isEmbeddedMode()) {
      return;
    }
    window.parent.postMessage({ type: MessageDataType.REQUEST_INIT }, '*');
    this.requestInitAttempts = 1;
    this.initRetryTimer = setInterval(() => {
      if (this.originWindow || this.requestInitAttempts >= 5) {
        clearInterval(this.initRetryTimer);
        return;
      }
      window.parent.postMessage({ type: MessageDataType.REQUEST_INIT }, '*');
      this.requestInitAttempts++;
    }, 500);
  }

  private getErrorContext() {
    return {
      initReceived: this.initReceived,
      requestInitAttempts: this.requestInitAttempts,
      checkoutContext: this.checkoutContext ?? null,
      msSinceConstruction: Date.now() - this.constructedAt,
    };
  }

  private handleMessages = (message: MessageEvent<IMessageData>) => {
    if (!message || !message.source || !message.data || !message.data.type || !message.data.checkoutContext) {
      if (message?.data?.type && Object.values(MessageDataType).includes(message.data.type)) {
        datadogRum.addError(new Error(EventsNames.messageServiceMessageDropped), {
          type: message.data.type,
          hasSource: !!message?.source,
          hasCheckoutContext: !!message?.data?.checkoutContext,
          origin: message?.origin,
          ...this.getErrorContext(),
        });
      }
      return;
    }

    switch (message.data.type) {
      case MessageDataType.INIT:
        this.originWindow = message.source;
        this.checkoutContext = message.data.checkoutContext;
        this.initReceived = true;
        if (this.initRetryTimer) {
          clearInterval(this.initRetryTimer);
        }
        break;
      default:
        break;
    }
  };

  public messageDone(purchaseId: string) {
    if (!this.originWindow) {
      console.error('no originWindow no message sent');
      datadogRum.addError(new Error(EventsNames.messageServiceDoneNoOriginWindow), this.getErrorContext());
      return;
    }
    console.log('messageDone', { checkoutContext: this.checkoutContext, purchaseId, type: MessageDataType.DONE });
    this.originWindow.postMessage(
      { checkoutContext: this.checkoutContext, purchaseId, type: MessageDataType.DONE },
      this.targetOrigin
    );
  }

  public messagePurchaseVerified(purchaseId: string) {
    if (!this.originWindow) {
      console.error('no originWindow no message sent');
      datadogRum.addError(new Error(EventsNames.messageServicePurchaseVerifiedNoOriginWindow), this.getErrorContext());
      return;
    }
    console.log('messagePurchaseVerified', {
      checkoutContext: this.checkoutContext,
      purchaseId,
      type: MessageDataType.PURCHASE_VERIFIED,
    });
    this.originWindow.postMessage(
      { checkoutContext: this.checkoutContext, purchaseId, type: MessageDataType.PURCHASE_VERIFIED },
      this.targetOrigin
    );
  }

  public messageCancel(purchaseId?: string) {
    if (!this.originWindow) {
      console.error('no originWindow no message sent');
      datadogRum.addError(new Error(EventsNames.messageServiceCancelNoOriginWindow), this.getErrorContext());
      return;
    }
    console.log('messageCancel', { checkoutContext: this.checkoutContext, purchaseId, type: MessageDataType.CANCEL });
    this.originWindow.postMessage(
      { checkoutContext: this.checkoutContext, purchaseId, type: MessageDataType.CANCEL },
      this.targetOrigin
    );
  }

  public messageUserCancel(purchaseId?: string) {
    if (!this.originWindow) {
      console.error('no originWindow no message sent');
      datadogRum.addError(new Error(EventsNames.messageServiceUserCancelNoOriginWindow), this.getErrorContext());
      return;
    }
    console.log('messageUserCancel', {
      checkoutContext: this.checkoutContext,
      purchaseId,
      type: MessageDataType.USER_CANCEL,
    });
    this.originWindow.postMessage(
      { checkoutContext: this.checkoutContext, purchaseId, type: MessageDataType.USER_CANCEL },
      this.targetOrigin
    );
  }

  public messagePlaceInFlow(page: PlaceInFlowPages, purchaseId?: string | null) {
    if (!this.originWindow) {
      console.error('no originWindow no message sent');
      datadogRum.addError(new Error(EventsNames.messageServicePlaceInFlowNoOriginWindow), this.getErrorContext());
      return;
    }
    console.log('messagePlaceInFlow', {
      checkoutContext: this.checkoutContext,
      page,
      type: MessageDataType.PLACE_IN_FLOW,
      purchaseId,
    });
    this.originWindow.postMessage(
      {
        checkoutContext: this.checkoutContext,
        page,
        type: MessageDataType.PLACE_IN_FLOW,
        purchaseId,
      },
      this.targetOrigin
    );
  }
}
